import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormProps,
  Input,
  Modal,
  notification,
  Radio,
  Select,
  Tabs
} from 'antd';

import useModal from 'stores/useModal';
import useCommonStore from 'stores/useCommon';
import { useGetMe } from 'hooks/useGetMe';
import { useMutation } from '@tanstack/react-query';
import {
  buildTransactionsInvoice,
  einvoiceUpdateCustomerInit,
  retryBuildTransactionsInvoice,
  updateInvoiceInfo
} from 'services/api/module/booking.api';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import {
  useGetGuestOrderDetail,
  useGetGuestOrders,
  useGetMinvoicePreview,
  useGetTransactionsFromOrders
} from 'hooks/useGetGuestTransactions';
import BillPDFv2 from 'components/bill-pdf/v2';
import EInvoiceTemplate from 'components/bill-pdf/v2/e-invoice-template';
import { useGetGroupBookingDetail } from 'hooks/useGetGroupBookings';
import { UpdateInvoiceInfoBody } from 'services/api/type/booking.type';
import { useTranslation } from 'react-i18next';
import InvoicePreviewModal from 'components/modal/invoice-preview';
import debounce from 'lodash/debounce';
import { useGetTravelAgencies, useGetTravelAgency } from 'hooks/useGetTravelAgencies';
import { EmailRegex } from 'constants/regex';
import { getMessageCode } from 'constants/validate';
import { useGetBookingDetail } from 'hooks/useGetBookingDetail';
import 'styles/print-invoice-v2.scss';
import { toFormatDate, toLocalTime } from 'utils';
import { currencyFormatter } from 'utils/currency';
import { PermissionActionTypes } from 'helper/type/action.type';
import IBButton from 'helper/permission.action';
import NikBillPDFv2 from 'components/bill-pdf/v2/components/nik';
// import { VATInvoiceGenerator, type InvoiceData } from 'components/bill-pdf/v2/test';

// const sampleData: InvoiceData = {
//   serialNo: 'IC25MSV',
//   invoiceNo: '106',
//   date: '04 tháng 08 năm 2025',
//   taxCode: 'MI-25-MDU-T7-0000007124',
//   seller: {
//     name: 'M-INVOICE - KIỂM THÚ HĐDT CÓ MÃ',
//     taxCode: '0106026495-999',
//     address: 'Số nhà 16, ngõ 269/1, đường Giáp Bát, Phường Giáp Bát, Quận Hoàng Mai, Hà Nội',
//     phone: '090469126',
//     email: 'hungkq@minvoice.vn',
//     bankAccount: '1234567890',
//     bank: 'ACB Hội sở'
//   },
//   buyer: {
//     name: 'NGUYỄN CHÁNH LONG',
//     company: 'M-invoice kiểm thử HĐDT có mã',
//     address: 'Hà Nội',
//     taxCode: '0106026495-999',
//     identityCard: '',
//     paymentMethod: 'TM/CK'
//   },
//   items: [
//     {
//       stt: 1,
//       description: 'Phòng Deluxe Double City View (ngày 04/08/2025)',
//       unit: 'Đêm',
//       quantity: 1.0,
//       unitPrice: 1620370.0,
//       amountBeforeVAT: 1620370,
//       vatRate: '8%',
//       vatAmount: 129630,
//       totalAmount: 1750000
//     }
//   ],
//   totalBeforeVAT: 1620370,
//   totalVATAmount: 129630,
//   totalAmount: 1750000,
//   amountInWords: 'Một triệu bảy trăm năm mươi nghìn đồng'
// };

// const generator = new VATInvoiceGenerator();
// generator.generateInvoice(sampleData);
// generator.save('invoice.pdf');

const today = dayjs();

export enum CHARGE_TYPE {
  ALL = 'all',
  ROOM = 'room',
  SERVICE = 'service'
}

const MemoizedBillPDFv2 = React.memo(BillPDFv2);
const MemoizedNikBillPDFv2 = React.memo(NikBillPDFv2);

function PrintInvoiceModalV2() {
  const { t } = useTranslation();
  const MESSAGE_CODE = getMessageCode(t);
  const [form] = Form.useForm();
  const {
    setIsOpenSignature,
    isOpenViewGroupInvoice,
    setIsOpenViewGroupInvoice,
    group_id,
    setInfoConfirmModal,
    createInvoiceTransaction,
    closeCreateInvoiceTransaction
  } = useModal();

  const { data: dataGroup } = useGetGroupBookingDetail(Number(group_id));

  const { currentBranch } = useCommonStore();

  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [travelAgencyInput, setTravelAgencyInput] = useState<string>('');
  const [keyDate, setKeyDate] = useState<number | undefined>(undefined);
  const [isEmailCcChecked, setIsEmailCcChecked] = useState<boolean>(false);
  const [isEInvoiceChecked, setIsEInvoiceChecked] = useState<boolean>(false);

  const { data: travelAgencies, isLoading } = useGetTravelAgencies({ search: travelAgencyInput });

  const { data: bookingLineDetail } = useGetBookingDetail(
    Number(createInvoiceTransaction.booking_line_id)
  );
  const { data: orderDetail } = useGetGuestOrderDetail(createInvoiceTransaction.currentOrderId);
  const { data: orders } = useGetGuestOrders(Number(createInvoiceTransaction?.guest_id));
  const { data: travelAgency } = useGetTravelAgency(
    bookingLineDetail?.hotel_travel_agency_id,
    !orderDetail?.invoice_info
  );
  const { data: debitTravelAgency } = useGetTravelAgency(
    orderDetail?.debit_travel_agency_id || undefined,
    true
  );

  const currentOrder = useMemo(() => {
    return orders
      ?.filter((item: any) => item?.folio_balance_code === createInvoiceTransaction.code)
      .at(0);
  }, [createInvoiceTransaction, orders]);

  const { data: minvoicePreviewPDF } = useGetMinvoicePreview(
    Number(orderDetail?.id),
    currentOrder?.state === 'invoiced'
  );

  const { data: transactionPayment } = useGetTransactionsFromOrders(
    bookingLineDetail?.booking_line_guest_ids?.join(','),
    orderDetail?.id
  );
  const { data: me } = useGetMe();

  const [dataChange, setDataChange] = useState<any>({});

  const { mutateAsync: UpdateInvoiceDetails } = useMutation({
    mutationFn: async (values: any) => {
      const data: UpdateInvoiceInfoBody = {
        partner_name: values?.partnerName || '',
        company_name: values.company || '',
        company_address: values.companyAddress || '',
        tax_code: values.taxCode || '',
        email: values.email || '',
        email_cc: (isEmailCcChecked && values.emailCc) || undefined,
        id_card: values.idCard || '',
        passport: values.passport || '',
        unrequested_einvoice: !isEInvoiceChecked
      };

      await updateInvoiceInfo(Number(orderDetail?.id), data);
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_DETAIL, Number(orderDetail?.id)]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
      });
    }
  });

  const { mutateAsync: mutateBuildTransactionsInvoice } = useMutation({
    mutationFn: (params: any) =>
      buildTransactionsInvoice({
        guest_order_id: params.guest_order_id,
        publish_einvoice: params.publish_einvoice,
        name: params.name,
        vat: params.vat,
        email: params.email,
        company_name: params.company_name,
        address: params.address,
        einvoice_option: params.einvoice_option,
        invoiceType: params.invoiceType,
        id_card: params.id_card,
        passport: params.passport
      })
  });

  const { mutateAsync: mutateRetryBuildTransactionsInvoice } = useMutation({
    mutationFn: (params: any) =>
      retryBuildTransactionsInvoice({
        guest_order_id: params.guest_order_id,
        publish_einvoice: params.publish_einvoice,
        name: params.name,
        vat: params.vat,
        email: params.email,
        company_name: params.company_name,
        address: params.address,
        einvoice_option: params.einvoice_option,
        invoiceType: params.invoiceType,
        id_card: params.id_card,
        passport: params.passport
      })
  });

  const { mutateAsync: mutateEinvoiceUpdateCustomer } = useMutation({
    mutationFn: (params: any) =>
      einvoiceUpdateCustomerInit({
        guest_order_id: params.guest_order_id,
        name: params.name,
        vat: params.vat,
        email: params.email,
        company_name: params.company_name,
        address: params.address,
        unrequested_einvoice: params.unrequested_einvoice,
        id_card: params.idCard,
        passport: params.passport
      })
  });

  const [signatureStaff, setSignatureStaff] = useState('');
  const [signatureGuest, setSignatureGuest] = useState('');
  const [invoiceError, setInvoiceError] = useState<string>('');
  const [showInvoiceError, setShowInvoiceError] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('receipt');

  // Disable all form items when currentOrder?.order_type !== 'pos' && currentOrder?.einvoice_signed
  const isFormDisabled = currentOrder?.order_type !== 'pos' && currentOrder?.einvoice_signed;

  useEffect(() => {
    if (!createInvoiceTransaction.isOpenCreateInvoice || !bookingLineDetail || !orderDetail) return;

    const mainGuest = bookingLineDetail?.customers.find(item => item.guest?.main_guest);

    const idCard =
      mainGuest?.country?.name === 'Vietnam' && !orderDetail?.invoice_info
        ? mainGuest?.identification
        : undefined;
    const passport =
      mainGuest?.country?.name !== 'Vietnam' && !orderDetail?.invoice_info
        ? mainGuest?.identification
        : undefined;
    const address =
      mainGuest?.country?.name === 'Vietnam' && !orderDetail?.invoice_info
        ? mainGuest?.contact_address_complete
        : mainGuest?.country?.name;

    const inv = orderDetail.invoice_info;

    const debitPreferredPartnerName = inv
      ? inv.partner_name ??
        orderDetail?.debit_partner_name ??
        orderDetail?.guest_name ??
        bookingLineDetail?.partner_name ??
        ''
      : orderDetail?.debit_partner_name ||
        orderDetail?.guest_name ||
        bookingLineDetail?.partner_name ||
        '';

    const debitPreferredCompany = inv
      ? inv.company_name ?? debitTravelAgency?.invoice_name ?? travelAgency?.name ?? ''
      : debitTravelAgency?.invoice_name || travelAgency?.name || '';

    const debitPreferredCompanyAddress = inv
      ? inv.company_address ?? debitTravelAgency?.address ?? travelAgency?.address ?? address ?? ''
      : debitTravelAgency?.address || travelAgency?.address || address || '';

    const debitPreferredTaxCode = inv
      ? inv.tax_code ?? debitTravelAgency?.vat ?? travelAgency?.vat ?? ''
      : debitTravelAgency?.vat || travelAgency?.vat || '';

    const initData = {
      chargeType: CHARGE_TYPE.ALL,
      createdDate: today,
      saleOrder: orderDetail?.sale_order_name || bookingLineDetail?.sale_order_name,
      partnerName: debitPreferredPartnerName,
      company: debitPreferredCompany,
      companyAddress: debitPreferredCompanyAddress,
      taxCode: debitPreferredTaxCode,
      email: inv ? inv.email ?? travelAgency?.email : travelAgency?.email,
      emailCc: inv ? inv.email_cc : undefined,
      idCard: inv ? inv.id_card ?? idCard ?? '' : idCard || '',
      passport: inv ? inv.passport ?? passport ?? '' : passport || ''
    };

    form.setFieldsValue(initData);
    setDataChange(initData);
    setSignatureStaff('');
    setSignatureGuest('');
    setIsEmailCcChecked(!!orderDetail?.invoice_info?.email_cc);
    setIsEInvoiceChecked(
      currentOrder.einvoice_signed
        ? currentOrder.einvoice_option === 'requested'
        : (!!orderDetail?.invoice_info && !orderDetail?.invoice_info?.unrequested_einvoice) || false
    );
    // eslint-disable-next-line
  }, [
    bookingLineDetail,
    orderDetail,
    createInvoiceTransaction.isOpenCreateInvoice,
    travelAgency,
    debitTravelAgency,
    form,
    currentOrder
  ]);

  const handleCancel = () => {
    setIsOpenViewGroupInvoice(false);
    closeCreateInvoiceTransaction();
    setIsPreviewOpen(false);
    setInvoiceError('');
    setShowInvoiceError(false);
    form.resetFields();
  };

  const handleClosePreviewModal = () => {
    setIsPreviewOpen(false);
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    setDataChange(values);
    await UpdateInvoiceDetails(values);
    setKeyDate(Date.now());
    // const requestedEInvoice = currentOrder?.einvoice_option === 'requested';
    // const finalRequestedEInvoice =
    //   currentOrder?.einvoice_published === false ? requestedEInvoice : isEInvoiceChecked;
    if (
      currentOrder.order_type !== 'pos'
      // currentOrder.einvoice_published &&
      // !currentOrder?.einvoice_signed
    ) {
      await mutateEinvoiceUpdateCustomer({
        guest_order_id: currentOrder.id,
        name: values.partnerName,
        vat: values.taxCode,
        email: values.emailCc ? `${values.email}, ${values.emailCc}` : values.email,
        company_name: values.company,
        address: values.companyAddress,
        unrequested_einvoice: !isEInvoiceChecked,
        id_card: values.idCard,
        passport: values.passport
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_MINVOICE_PREVIEW, currentOrder.id]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS]
      });
    }
  };

  const handleOpenSignatureStaff = () => {
    setIsOpenSignature(true, {
      title: t('common.signature.staff'),
      onOk: (data: any) => {
        setSignatureStaff(data);
      }
    });
  };

  const handleOpenSignatureGuest = () => {
    setIsOpenSignature(true, {
      title: t('common.signature.customer'),
      onOk: (data: any) => {
        setSignatureGuest(data);
      }
    });
  };

  const handleBuildInvoice = async () => {
    const data = form.getFieldsValue();

    if (!createInvoiceTransaction.guest_id || !createInvoiceTransaction.code || !orderDetail)
      return;

    try {
      // Clear any previous errors only when starting a new request
      setInvoiceError('');
      setShowInvoiceError(false);

      const invoicePayload = {
        guest_order_id: orderDetail.id,
        publish_einvoice: true,
        name: data.partnerName,
        vat: data.taxCode || undefined,
        email: data.emailCc ? `${data.email}, ${data.emailCc}` : data.email,
        company_name: data.company,
        address: data.companyAddress,
        einvoice_option: isEInvoiceChecked ? 'requested' : 'unrequested',
        invoiceType: orderDetail.order_type,
        id_card: data.idCard,
        passport: data.passport
      };

      if (currentOrder?.state === 'invoiced' && !currentOrder?.einvoice_published) {
        await mutateRetryBuildTransactionsInvoice(invoicePayload);
      } else {
        await mutateBuildTransactionsInvoice(invoicePayload);
      }

      notification.success({ message: t('common.message.createInvoice') });
      setIsPreviewOpen(true);

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, createInvoiceTransaction.guest_id]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
      });
      await queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.KEY_ROOM,
          QUERY_KEYS.GET_BOOKING_DETAIL,
          bookingLineDetail?.booking_line_id
        ]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_MINVOICE_PREVIEW, orderDetail.id]
      });
    } catch (error: any) {
      if (error.error.includes('M-invoice')) {
        const errorMessage = t('cashierPage.invoice.errorExportMInvoice');
        setInvoiceError(errorMessage);
        setShowInvoiceError(true);
      } else {
        notification.error({ message: error.error });
      }
    }
  };

  const handleConfirmExportMInvoice = async () => {
    const currentChargeType = form.getFieldsValue(['chargeType']);
    if (currentChargeType.chargeType !== CHARGE_TYPE.ALL) {
      return notification.error({ message: t('cashierPage.invoice.mustPayForAll') });
    }
    setInfoConfirmModal(true, {
      title: t('invoice.confirmExportTitle'),
      message: t('invoice.confirmExportMessage'),
      subMessage: t('invoice.confirmExportSubMessage'),
      importantMessage: t('invoice.confirmExportImportantMessage'),
      width: 510,
      okText: t('common.actions.accept'),
      cancelText: t('common.actions.cancelAction'),
      onOk: () => {
        handleBuildInvoice();
        setInfoConfirmModal(false);
      }
    });
  };

  const handlePreviewMinvoice = async () => {
    setIsPreviewOpen(true);
  };

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setTravelAgencyInput(value);
    }, 600),
    []
  );
  const handleGetTravelAgencyData = (value: string) => {
    debouncedSearch(value);
  };

  const pdfProps = useMemo(
    () => ({
      bookingLineDetail,
      orderDetail,
      me,
      dataChange,
      signatureStaff,
      signatureGuest,
      currentBranch,
      dataGroup,
      transactionPayment,
      invoiceError,
      showInvoiceError
    }),
    [
      bookingLineDetail,
      orderDetail,
      me,
      dataChange,
      signatureStaff,
      signatureGuest,
      currentBranch,
      dataGroup,
      transactionPayment,
      invoiceError,
      showInvoiceError
    ]
  );

  const sellerEInvoice = {
    seller: 'CÔNG TY TNHH KIN LIVING MANAGEMENT',
    sellerTaxCode: '0317936630',
    sellerAddress:
      'Số 13 Đường N2, Jamona Golden Silk, Phường Tân Thuận, Thành phố Hồ Chí Minh, Việt Nam'
  };

  const einvoiceData = orderDetail?.transactions.filter(item => !!item.uom);
  const eInvoiceItemDetails = einvoiceData
    ?.filter(item => !item.is_deleted)
    ?.map(item => ({
      name:
        item.transaction_code_name == 'Room Charge'
          ? `Đêm ${item.name} (${toFormatDate(item.transaction_date)})`
          : item.name,
      uom: item.uom,
      quantity: item.qty,
      unitPrice: item.tax_excluded,
      amountBeforeTax: item.tax_excluded,
      taxRate: item.tax,
      taxAmount: item.tax_amount,
      amountAftertax: item.total
    }));

  const totalsByTaxRateDetails = eInvoiceItemDetails?.reduce(
    (
      acc: Record<
        number,
        {
          totalBeforeTax: number;
          totalTax: number;
          totalAmount: number;
        }
      >,
      item
    ) => {
      const taxRate = item.taxRate;
      if (!acc[taxRate]) {
        acc[taxRate] = {
          totalBeforeTax: 0,
          totalTax: 0,
          totalAmount: 0
        };
      }
      acc[taxRate].totalBeforeTax += item.amountBeforeTax;
      acc[taxRate].totalTax += item.taxAmount;
      acc[taxRate].totalAmount += item.amountAftertax;
      return acc;
    },
    {} as Record<
      number,
      {
        totalBeforeTax: number;
        totalTax: number;
        totalAmount: number;
      }
    >
  );

  const totalByTaxRate = eInvoiceItemDetails?.reduce(
    (acc, item) => {
      acc.totalBeforeTax += item.amountBeforeTax;
      acc.totalTax += item.taxAmount;
      acc.totalAmount += item.amountAftertax;
      return acc;
    },
    {
      totalBeforeTax: 0,
      totalTax: 0,
      totalAmount: 0
    }
  );

  const invoiceInfo = orderDetail?.invoice_info;

  const eInvoiceCompanyName = isEInvoiceChecked
    ? invoiceInfo
      ? invoiceInfo.company_name ??
        debitTravelAgency?.invoice_name ??
        travelAgency?.name ??
        (form.getFieldValue('company') || '')
      : debitTravelAgency?.invoice_name || travelAgency?.name || form.getFieldValue('company') || ''
    : '';

  const eInvoiceCompanyAddress = isEInvoiceChecked
    ? invoiceInfo
      ? invoiceInfo.company_address ??
        debitTravelAgency?.address ??
        travelAgency?.address ??
        (form.getFieldValue('companyAddress') || '')
      : debitTravelAgency?.address ||
        travelAgency?.address ||
        form.getFieldValue('companyAddress') ||
        ''
    : '';

  const buyerNameBase = invoiceInfo
    ? invoiceInfo.partner_name ??
      orderDetail?.debit_partner_name ??
      form.getFieldValue('partnerName') ??
      orderDetail?.guest_name ??
      ''
    : orderDetail?.debit_partner_name ||
      form.getFieldValue('partnerName') ||
      orderDetail?.guest_name ||
      '';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const eInvoiceItems = {
    date: toLocalTime(new Date().toISOString(), 'DD/MM/YYYY'),
    serialNo: '1C25MKL',
    orderNo: orderDetail?.sale_order_name,
    buyerName: isEInvoiceChecked
      ? // ? hasEInvoiceCompany
        //   ? ''
        buyerNameBase
      : `Khách hàng không lấy hóa đơn - ${buyerNameBase}`,
    paymentMethod: 'TM/CK',
    identityCard: isEInvoiceChecked
      ? invoiceInfo
        ? invoiceInfo.id_card ?? (form.getFieldValue('idCard') || '')
        : form.getFieldValue('idCard') || ''
      : '',
    passport: isEInvoiceChecked
      ? invoiceInfo
        ? invoiceInfo.passport ?? (form.getFieldValue('passport') || '')
        : form.getFieldValue('passport') || ''
      : '',
    company: eInvoiceCompanyName,
    companyAddress: eInvoiceCompanyAddress,
    taxCode: isEInvoiceChecked
      ? invoiceInfo
        ? invoiceInfo.tax_code ??
          debitTravelAgency?.vat ??
          travelAgency?.vat ??
          (form.getFieldValue('taxCode') || '')
        : debitTravelAgency?.vat || travelAgency?.vat || form.getFieldValue('taxCode') || ''
      : '',
    sellerItems: sellerEInvoice,
    items: eInvoiceItemDetails,
    totalsByTaxRateDetails: totalsByTaxRateDetails,
    totalByTaxRate: totalByTaxRate,
    totalAmountInWords: currencyFormatter(totalByTaxRate ? totalByTaxRate.totalAmount : 0)
  };

  const items = useMemo(
    () => [
      {
        key: 'receipt',
        label: t('cashierPage.tabKeys.receipt'),
        children: (
          <div style={{ height: 'calc(115vh - 300px)', minHeight: '500px' }}>
            <PDFViewer width="98%" height="100%">
              {currentBranch?.is_nik_hotel ? (
                <MemoizedNikBillPDFv2 {...pdfProps} />
              ) : (
                <MemoizedBillPDFv2 {...pdfProps} />
              )}
            </PDFViewer>
          </div>
        )
      },
      {
        key: 'e-invoice',
        label: t('cashierPage.tabKeys.eInvoice'),
        children: (
          <div style={{ height: 'calc(115vh - 300px)', minHeight: '500px' }}>
            <PDFViewer width="98%" height="100%">
              <EInvoiceTemplate
                sellerInfo={eInvoiceItems.sellerItems}
                invoiceData={{
                  date: eInvoiceItems.date,
                  serialNo: eInvoiceItems.serialNo,
                  orderNo: eInvoiceItems.orderNo,
                  buyerName: eInvoiceItems.buyerName,
                  paymentMethod: eInvoiceItems.paymentMethod,
                  identityCard: eInvoiceItems.identityCard,
                  passport: eInvoiceItems.passport,
                  company: eInvoiceItems.company,
                  companyAddress: eInvoiceItems.companyAddress,
                  taxCode: eInvoiceItems.taxCode,
                  items: eInvoiceItems.items?.map(item => ({
                    name: item.name,
                    unit: item.uom,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    amountBeforeTax: item.amountBeforeTax,
                    taxRate: item.taxRate,
                    taxAmount: item.taxAmount,
                    amountAfterTax: item.amountAftertax
                  })),
                  totalBeforeTax: eInvoiceItems.totalByTaxRate?.totalBeforeTax,
                  totalTax: eInvoiceItems.totalByTaxRate?.totalTax,
                  totalAmount: eInvoiceItems.totalByTaxRate?.totalAmount,
                  totalAmountInWords: eInvoiceItems.totalAmountInWords,
                  totalsByTaxRate: eInvoiceItems.totalsByTaxRateDetails
                }}
              />
            </PDFViewer>
          </div>
        )
      }
    ],
    [pdfProps, eInvoiceItems, t, currentBranch?.is_nik_hotel]
  );

  return (
    <Modal
      title={t('cashierPage.previewPrint')}
      className="modal-view-bill-pdf"
      centered
      width="90vw"
      style={{ maxWidth: '1400px', minWidth: '800px' }}
      open={isOpenViewGroupInvoice || createInvoiceTransaction.isOpenCreateInvoice}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <div style={{ width: '100%', overflow: 'hidden' }}>
        <Tabs items={items} activeKey={activeTab} onChange={setActiveTab} />
      </div>
      <Form
        form={form}
        name="bill-pdf-form"
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        size="small"
        className="compact-form bill-pdf-form"
      >
        {activeTab !== 'e-invoice' && (
          <>
            <Form.Item
              name="chargeType"
              label={t('cashierPage.resolveDocument.paymentFor')}
              style={{ marginTop: 50 }}
            >
              <Radio.Group disabled={isFormDisabled}>
                <Radio value={CHARGE_TYPE.ALL}>{t('common.statuses.all')}</Radio>
                <Radio value={CHARGE_TYPE.ROOM}>{t('billPDFPage.roomCharge')}</Radio>
                <Radio value={CHARGE_TYPE.SERVICE}>{t('billPDFPage.serviceCharge')}</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="createdDate" label={t('managePage.transactionPage.v2.createdDate')}>
              <DatePicker format="DD/MM/YYYY" className="w-full" disabled />
            </Form.Item>
          </>
        )}
        <Form.Item
          name="partnerName"
          label={t('billPDFPage.customerName')}
          style={{ marginTop: activeTab === 'e-invoice' ? 50 : 0 }}
        >
          <Input disabled={isFormDisabled} />
        </Form.Item>
        <Form.Item
          name="company"
          label={t('billPDFPage.company')}

          // rules={
          //   currentOrder?.einvoice_option === 'requested' && currentOrder?.einvoice_published
          //     ? [
          //         {
          //           required: true
          //         }
          //       ]
          //     : []
          // }
        >
          <Select
            showSearch
            optionFilterProp="children"
            onSearch={handleGetTravelAgencyData}
            onChange={value => {
              if (value === undefined) {
                setTravelAgencyInput('');
                form.resetFields(['company', 'companyAddress', 'taxCode', 'email']);
              }
              const selectedAgency = travelAgencies?.find(agency => agency.id === value);
              if (selectedAgency) {
                form.setFieldsValue({
                  partnerName: '',
                  company: selectedAgency.invoice_name || selectedAgency.name,
                  companyAddress: selectedAgency.address || '',
                  taxCode: selectedAgency.vat || '',
                  email: selectedAgency.email || ''
                });
              }
            }}
            allowClear
            filterOption={false}
            loading={isLoading}
            dropdownStyle={{ maxHeight: '300px', overflowY: 'auto' }}
            disabled={isFormDisabled}
          >
            {travelAgencies?.map(agency => (
              <Select.Option key={agency.id} value={agency.id}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span>{agency.invoice_name}</span>
                  {(agency.name || agency.vat) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', opacity: 0.6 }}>
                      <span>{agency.name || ''}</span>
                      <span>{agency.vat || ''}</span>
                    </div>
                  )}
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="taxCode" label={t('billPDFPage.taxCode')}>
          <Input disabled={isFormDisabled} />
        </Form.Item>
        <Form.Item name="idCard" label={t('billPDFPage.idCard')}>
          <Input disabled={isFormDisabled} />
        </Form.Item>
        <Form.Item name="passport" label={t('billPDFPage.passport')}>
          <Input disabled={isFormDisabled} />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            {
              validator: async (_, value) => {
                const trimmed = (value ?? '').trim();
                if (!trimmed) {
                  if (isEInvoiceChecked) {
                    return Promise.reject(new Error(MESSAGE_CODE.REQUIRED_EMAIL));
                  }
                  return Promise.resolve();
                }
                if (!EmailRegex.test(trimmed)) {
                  return Promise.reject(new Error(MESSAGE_CODE.WRONG_FORMAT_EMAIL));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input disabled={isFormDisabled} />
        </Form.Item>
        <Form.Item name="companyAddress" label={t('common.customer.address')}>
          <Input disabled={isFormDisabled} />
        </Form.Item>
        <Form.Item style={{ marginTop: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Checkbox
              checked={isEmailCcChecked}
              onChange={e => setIsEmailCcChecked(e.target.checked)}
              disabled={isFormDisabled}
            >
              Cc
            </Checkbox>
            {isEmailCcChecked && (
              <Form.Item
                name="emailCc"
                noStyle
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!value) return Promise.resolve(); // Allow empty input if not required
                      const cleanedValue = value?.replace(/,+\s*$/, '').trim();
                      const emails = cleanedValue
                        .split(',')
                        .map((email: string) => email.trim())
                        .filter((email: string) => email);
                      if (emails.length > 4) {
                        form.setFieldsValue({ emailCc: emails.slice(0, 4).join(', ') });
                        return Promise.reject(new Error(t('cashierPage.invoice.maxFourEmails')));
                      }
                      const invalidEmails = emails.filter(
                        (email: string) => !EmailRegex.test(email)
                      );
                      if (invalidEmails.length > 0) {
                        return Promise.reject(new Error(MESSAGE_CODE.WRONG_FORMAT_EMAIL));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  placeholder={t('cashierPage.invoice.inputCcEmailAddress')}
                  style={{ flex: '1' }}
                  onChange={e => {
                    form.setFieldsValue({ emailCc: e.target.value });
                  }}
                  onBlur={e => {
                    const cleanedValue = e.target.value?.replace(/,+\s*$/, '').trim();
                    form.setFieldsValue({ emailCc: cleanedValue });
                  }}
                  disabled={isFormDisabled}
                />
              </Form.Item>
            )}
            <Checkbox
              checked={isEInvoiceChecked}
              onChange={e => {
                setIsEInvoiceChecked(e.target.checked);
                form.validateFields(['email']);
              }}
              disabled={isFormDisabled}
            >
              E-Invoice
            </Checkbox>
          </div>
        </Form.Item>
        <Button
          htmlType="submit"
          className="ant-btn-secondary w-full"
          disabled={currentOrder?.order_type !== 'pos' && currentOrder?.einvoice_signed}
        >
          {t('managePage.travelAgencyPage.taActions.saveButton')}
        </Button>
        <div className="flex" style={{ flexDirection: 'column', gap: '8px', marginTop: 16 }}>
          {activeTab !== 'e-invoice' && (
            <>
              <Button onClick={handleOpenSignatureStaff}>{t('common.signature.staff')}</Button>
              <Button onClick={handleOpenSignatureGuest}>{t('common.signature.customer')}</Button>
            </>
          )}
          <div style={{ border: '1px solid #9f9f9f', margin: '16px 0' }} />
          {currentOrder?.state === 'invoiced' && currentOrder?.einvoice_published ? (
            <Button
              key="preview_minvoice"
              type="primary"
              className="ant-btn-secondary w-full invoice-action-button"
              onClick={() => handlePreviewMinvoice()}
            >
              {t('cashierPage.invoice.previewMinvoice')}
            </Button>
          ) : (
            dayjs().isBefore(dayjs('2025-12-31 23:59:59')) && (
              <IBButton
                key="create_invoice"
                permission={PermissionActionTypes.CREATE_INVOICE_MANUAL}
                type="primary"
                className="ant-btn-secondary w-full invoice-action-button"
                onClick={() => handleConfirmExportMInvoice()}
              >
                {t('cashierPage.invoice.createInvoice')}
              </IBButton>
            )
          )}
        </div>
      </Form>
      <InvoicePreviewModal
        isOpen={isPreviewOpen}
        previewUrl={minvoicePreviewPDF?.preview_url}
        guestOrderId={currentOrder?.id}
        currentOrder={currentOrder}
        onClose={handleClosePreviewModal}
        setKeyDate={() => setKeyDate(Date.now())}
        key={keyDate}
      />
    </Modal>
  );
}

export default PrintInvoiceModalV2;
